import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faTrash } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import ModalDialog from '../../components/modal_dialog';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import { StatementImportModal } from './StatementImportModal';
import StatementImportSettingsModal from './StatementImportSettingsModal';
import { formatCard, formatDate, formatPeriod } from './statement-display';
import AccessControl from '../../utils/access_control';

import './statement-imports.scss';

// A statement that is still being processed resolves on its own, so the list
// re-reads itself until nothing is pending. The ceiling keeps a statement that
// never resolves from polling for the life of the page.
const PROCESSING_POLL_INTERVAL_MS = 10 * 1000;
const PROCESSING_POLL_MAX_MS = 10 * 60 * 1000;

const columns = [
  { id: 'filename', label: 'cc_statements.statement_imports.table.filename' },
  { id: 'card', label: 'cc_statements.statement_imports.table.card' },
  { id: 'issuer', label: 'cc_statements.statement_imports.table.issuer' },
  { id: 'period', label: 'cc_statements.statement_imports.table.period' },
  { id: 'matched', label: 'cc_statements.statement_imports.table.matched', align: 'right' },
  { id: 'status', label: 'cc_statements.statement_imports.table.status' },
  { id: 'uploaded', label: 'cc_statements.statement_imports.table.uploaded' },
];

/**
 * Introduces statement imports and links to the dedicated upload page.
 *
 * @param {object} props
 * @param {object} props.data - MobX data store containing the translator.
 * @returns {JSX.Element} The statement-import call-to-action.
 */
const StatementImportsSection = ({ data }) => {
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const canEditClientConfig = AccessControl.canAccessAdminSection(data, 'clients');

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadReconciliations = useCallback(() => {
    apiFetch(
      '/c_card/statement-reconciliations',
      'GET',
      {},
      (response) => {
        if (!mountedRef.current) return;
        setReconciliations(response.data?.items || []);
        setLoadFailed(false);
        setLoading(false);
      },
      () => {
        if (!mountedRef.current) return;
        setLoadFailed(true);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    loadReconciliations();
  }, [loadReconciliations]);

  // Processing finishes on the API's own schedule with nothing to notify us, so
  // the list re-reads itself while any row is still pending and stops as soon
  // as none is.
  const hasProcessing = reconciliations.some(
    (reconciliation) => reconciliation.status === 'processing'
  );

  useEffect(() => {
    if (!hasProcessing) return undefined;

    const interval = setInterval(loadReconciliations, PROCESSING_POLL_INTERVAL_MS);
    const ceiling = setTimeout(() => clearInterval(interval), PROCESSING_POLL_MAX_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(ceiling);
    };
  }, [hasProcessing, loadReconciliations]);

  const selectableIds = reconciliations.map((reconciliation) => reconciliation.id);
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleOne = (id, checked) => {
    setSelectedIds((current) => (
      checked ? [...current, id] : current.filter((value) => value !== id)
    ));
  };

  const toggleAll = (checked) => setSelectedIds(checked ? selectableIds : []);

  /** Soft-delete the selection in one request, the way the sibling tables do. */
  const deleteSelected = () => {
    setDeleting(true);

    apiFetch(
      '/c_card/statement-reconciliations',
      'DELETE',
      { reconciliation_ids: selectedIds },
      () => {
        setReconciliations((current) => current.filter((item) => !selectedIds.includes(item.id)));
        setSelectedIds([]);
        setDeleting(false);
        setConfirmingDelete(false);
        toast.success(data.t.read('cc_statements.statement_imports.delete_successful'));
      },
      () => {
        setDeleting(false);
        setConfirmingDelete(false);
        toast.error(data.t.read('cc_statements.statement_imports.delete_failed'));
      }
    );
  };

  return (
    <section className='statement-imports mb-5' aria-labelledby='statement-imports-title'>
      <div className='statement-imports__header'>
        <div>
          <h2 id='statement-imports-title'>{data.t.read('cc_statements.statement_imports.title')}</h2>
          <p>{data.t.read('cc_statements.statement_imports.description')}</p>
        </div>
        <div className='d-flex align-items-center'>
          <button
            type='button'
            className='btn btn-danger'
            disabled={selectedIds.length === 0 || deleting}
            onClick={() => setConfirmingDelete(true)}
          >
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_statements.statement_imports.delete_selected')}
          </button>
          <Link className='btn btn-primary ml-3' to='/c_card/statements/add'>
            {data.t.read('cc_statements.statement_imports.upload_button')}
          </Link>
          {canEditClientConfig && (
            <button type='button' className='btn btn-outline-secondary ml-2'
              aria-label={data.t.read('cc_statements.statement_imports.statement_import_settings.open')}
              title={data.t.read('cc_statements.statement_imports.statement_import_settings.open')}
              onClick={() => setShowSettings(true)}>
              <FontAwesomeIcon icon={faCog} />
            </button>
          )}
        </div>
      </div>

      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', border: '1px solid #c1c7d0' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table
            stickyHeader
            aria-label={data.t.read('cc_statements.statement_imports.table.label')}
            sx={{ '& .MuiTableCell-root': { fontSize: '1rem' } }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' sx={{ backgroundColor: '#f4f5f7' }}>
                  <Checkbox
                    color='primary'
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={selectableIds.length === 0}
                    inputProps={{
                      'aria-label': data.t.read('cc_statements.statement_imports.select_all'),
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleAll(event.target.checked)}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ backgroundColor: '#f4f5f7', fontWeight: 600, fontSize: '1rem' }}
                  >
                    {data.t.read(column.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <div className='d-flex justify-content-center'>
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && loadFailed && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center' role='alert'>
                    {data.t.read('cc_statements.statement_imports.table.load_failed')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadFailed && reconciliations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center'>
                    {data.t.read('cc_statements.statement_imports.table.empty')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadFailed && reconciliations.map((reconciliation) => {
                // A statement still being processed has no lines, no card and no
                // matches yet, so there is nothing to open: the row stays inert
                // until the import resolves.
                const isProcessing = reconciliation.status === 'processing';

                return (
                  <TableRow
                    key={reconciliation.id}
                    hover={!isProcessing}
                    tabIndex={isProcessing ? undefined : 0}
                    role={isProcessing ? undefined : 'button'}
                    aria-label={reconciliation.filename || reconciliation.id}
                    aria-disabled={isProcessing || undefined}
                    className={`statement-imports__row${isProcessing ? ' statement-imports__row--processing' : ''}`}
                    onClick={isProcessing ? undefined : () => setSelectedReconciliation(reconciliation)}
                    onKeyDown={(event) => {
                      if (isProcessing) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      // Space belongs to the checkbox when it has focus; it must
                      // not also open the statement behind it.
                      if (event.target.type === 'checkbox') return;
                      event.preventDefault();
                      setSelectedReconciliation(reconciliation);
                    }}
                  >
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={selectedIds.includes(reconciliation.id)}
                        inputProps={{
                          'aria-label': data.t.read('cc_statements.statement_imports.select_row'),
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => toggleOne(reconciliation.id, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{reconciliation.filename || '—'}</TableCell>
                    <TableCell>{formatCard(reconciliation.card_account)}</TableCell>
                    <TableCell>{reconciliation.issuer_name || '—'}</TableCell>
                    <TableCell>{formatPeriod(reconciliation.statement)}</TableCell>
                    {/* Zero matches is a result; a statement mid-processing has none yet. */}
                    <TableCell align='right'>
                      {isProcessing ? '—' : reconciliation.matching?.matched_count ?? 0}
                    </TableCell>
                    <TableCell>
                      <span className={`statement-imports__status statement-imports__status--${reconciliation.status}`}>
                        {data.t.read(`cc_statements.statement_imports.status.${reconciliation.status}`)}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(reconciliation.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {confirmingDelete && (
        <ModalDialog
          variant='confirm'
          destructive
          title={data.t.read('cc_statements.statement_imports.delete_title')}
          subtitle={`${data.t.read('cc_statements.statement_imports.delete_confirm_message')} (${selectedIds.length})`}
          cancelAction={() => setConfirmingDelete(false)}
          cancelText={data.t.read('cancel')}
          okAction={() => {
            deleteSelected();
            // Held open by `deleting` until the request answers.
            return false;
          }}
          okText={data.t.read('delete')}
          okLoading={deleting}
          invalid={deleting}
          noProcessingLoading
        />
      )}

      {selectedReconciliation && (
        <StatementImportModal
          data={data}
          reconciliation={selectedReconciliation}
          onClose={() => setSelectedReconciliation(null)}
        />
      )}

      {showSettings && <StatementImportSettingsModal data={data} onClose={() => setShowSettings(false)} />}
    </section>
  );
};

export default StatementImportsSection;
